import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getTabs, updateTab as apiUpdateTab } from '../api/tabs.api.js';
import { fireKitchenOrder } from '../api/kitchen.api.js';
import { isOfflineError } from '../api/client.js';
import { mergeTabItems } from '../lib/tabMerge.js';
import { useSyncStore } from './syncStore.js';
import { useAuthStore } from './authStore.js';

// ─── De qué comercio es cada cosa ─────────────────────────────────────────────
//
// La cola vive en el aparato, y un aparato puede entrar a otro comercio: una
// tablet de prueba, una que se presta, una que cambia de dueño. Sin marca, lo
// pendiente se mandaba con la sesión del comercio que estuviera adentro en ese
// momento, y una comanda de un restaurante podía salir en la cocina de otro.
//
// Cada entrada guarda el comercio con el que se capturó y solo se manda cuando
// se está dentro de ese mismo comercio. Lo de otros se queda quieto: si se
// vuelve a entrar a su comercio, sale entonces.
const comercioActual = () => {
  const t = useAuthStore.getState().tenantId;
  return t ? String(t) : null;
};
export const esDelComercio = (entrada, comercio = comercioActual()) =>
  !!comercio && entrada?.tenantId != null && String(entrada.tenantId) === comercio;

// ─── Outbox del dispositivo ───────────────────────────────────────────────────
//
// El hub ya tenía su propia outbox, pero cubre el tramo caja↔nube. El tramo
// mesero↔caja no tenía ninguna: al fallar la red, la orden se perdía sin que
// la pantalla lo dijera. Una tablet clavada en la barra casi nunca se cae del
// wifi; un mesero caminando, sí.
//
// Lo que se encola es la INTENCIÓN, no la petición HTTP. Un PATCH guardado
// lleva dentro una `version` que envejece: al reintentarlo chocaría con 409
// para siempre. Guardamos "esta cuenta tiene cambios locales sin confirmar" y
// al drenar se vuelve a fundir contra la versión fresca del servidor, que es
// exactamente lo que hace `saveActive` cuando hay red.
//
// Hay como mucho UNA entrada por cuenta: si el mesero agrega tres platillos
// sin señal, lo que queda pendiente es el estado final de esa cuenta, no tres
// eventos. El hub hace lo mismo con su `tab.upsert`.

// Aviso de "esta cuenta ya quedó confirmada". Lo registra App.jsx —que sí
// puede importar las dos stores— para que `tabsStore` mueva su base.
//
// Sin esto la cola sube el cambio pero la base local se queda atrás, y la
// SIGUIENTE diferencia se calcula contra una base vieja: el servidor recibe
// +2 donde el mesero agregó +1. Perder una orden es malo; cobrarle al cliente
// un platillo que no pidió es peor.
let alConfirmar = null;
export const registrarConfirmacion = (fn) => { alConfirmar = fn; };

// Después de esto, el problema no es la red: es que el servidor rechaza el
// contenido. Se deja de reintentar y se marca para que alguien lo mire.
const MAX_INTENTOS = 8;

// Cuánto puede esperar una comanda antes de dejar de tener sentido mandarla.
//
// No es un límite técnico: una comanda que se capturó a las 11:58 y no salió
// porque cerraron la caja NO debe entrar sola en el turno de la mañana
// siguiente. Esa mesa ya se fue, ese platillo nunca se preparó, y meterlo en
// el turno de mañana le inventa una venta a alguien más. Pasado este plazo se
// aparta para que una persona decida.
const MAX_ESPERA_MS = 4 * 60 * 60 * 1000;

// Espera creciente entre reintentos de algo que el negocio todavía no puede
// recibir. Sin esto se golpea el servidor cada 15 segundos toda la noche.
const esperaTras = (intentos) => Math.min(15000 * 2 ** intentos, 5 * 60 * 1000);

// Códigos donde reintentar no tiene sentido: el servidor entendió y dijo que
// no. El 404 es el caso más común y el más benigno — la cuenta se cobró o se
// borró en otra tablet, así que lo local ya no aplica.
const DEFINITIVOS = [400, 401, 403, 404, 409, 422];

export const useOutboxStore = create(
  persist(
    (set, get) => ({
      // { [tabId]: { tabId, items, customer, comensales, discount, note, baseItems, attempts, lastError, since } }
      pendientes: {},
      // Comandas que el servidor rechazó: no se reintentan solas, pero tampoco
      // se tiran en silencio — alguien tiene que enterarse de que esa mesa no
      // llegó a cocina.
      fallidas:   [],

      // Comandas sin confirmar, en orden de captura.
      //
      // Estas NO se fusionan como las cuentas. Una cuenta es un estado: lo que
      // importa es cómo quedó. Una comanda es un evento: si el mesero marchó
      // la entrada y diez minutos después el fuerte, son dos papeles distintos
      // y la cocina tiene que recibir los dos, en ese orden.
      //
      // Se pueden reintentar sin miedo porque el servidor es idempotente por
      // `client_uuid` y responde `created:false` si ya la tenía.
      comandas:   [],

      drenando:   false,

      // ── Cola ─────────────────────────────────────────────────────────────

      encolar: ({ tabId, items, customer, comensales, discount, note, baseItems }) => {
        set((s) => ({
          pendientes: {
            ...s.pendientes,
            [tabId]: {
              tabId, items, customer, comensales, discount, note,
              tenantId: comercioActual(),
              // La base es la versión de la cuenta sobre la que el mesero hizo
              // sus cambios. Sin ella no se puede saber qué parte del carrito
              // es SUYA, y al drenar duplicaríamos lo que ya estaba.
              baseItems,
              // Se conserva la del intento anterior: lo que interesa es desde
              // cuándo está esperando, no cuándo se reintentó por última vez.
              since:     s.pendientes[tabId]?.since ?? new Date().toISOString(),
              attempts:  s.pendientes[tabId]?.attempts ?? 0,
              lastError: null,
            },
          },
        }));
        get()._publicarContadores();
      },

      quitar: (tabId) => {
        set((s) => {
          const { [tabId]: _, ...resto } = s.pendientes;
          return { pendientes: resto };
        });
        get()._publicarContadores();
      },

      encolarComanda: (payload) => {
        set((s) => ({ comandas: [...s.comandas, { payload, tenantId: comercioActual(), attempts: 0, since: new Date().toISOString() }] }));
        get()._publicarContadores();
      },

      // ── Drenado ──────────────────────────────────────────────────────────

      /**
       * Intenta subir todo lo pendiente. Es seguro llamarlo de más: si ya hay
       * un drenado en curso, se ignora.
       *
       * Devuelve cuántas cuentas quedaron confirmadas.
       */
      drenar: async () => {
        const { pendientes, comandas, drenando } = get();
        // Solo lo que se capturó en el comercio en el que se está ahora, y solo
        // lo que toca: las cuentas rechazadas de verdad (`fallida`) esperan a
        // que alguien decida en Pendientes —reintentarlas cada 15 s no las iba
        // a arreglar—, y una comanda en espera no se reintenta antes de tiempo.
        // Sin esto, una sola comanda en espera pedía todas las cuentas abiertas
        // al servidor cada 15 s, en cada aparato.
        const ids = Object.keys(pendientes).filter((id) => esDelComercio(pendientes[id]) && !pendientes[id].fallida);
        const ahora = Date.now();
        const comandaLista = comandas.some((c) => esDelComercio(c) && !(c.nextTryAt && Date.parse(c.nextTryAt) > ahora));
        if (drenando || (!ids.length && !comandaLista)) return 0;

        set({ drenando: true });
        let confirmadas = 0;

        try {
          // Una sola lectura para todas: al drenar hay que fundir contra lo
          // que el servidor tenga AHORA, no contra lo que había cuando se
          // encoló. Si esto falla, seguimos sin red y no hay nada que hacer.
          let remotas;
          try {
            remotas = await getTabs('open');
          } catch (err) {
            if (isOfflineError(err)) return 0;
            throw err;
          }

          // Las comandas van primero y en orden: la cocina empieza a preparar
          // en cuanto llegan, así que cada minuto que esperan es servicio
          // retrasado. Las cuentas pueden aguantar un poco más.
          // En orden, pero saltando las de otros comercios sin moverlas.
          while (get().comandas.some((c) => esDelComercio(c))) {
            const siguiente = get().comandas.find((c) => esDelComercio(c));
            const sin = (lista) => lista.filter((c) => c !== siguiente);

            // Esperando a que abran caja: todavía no toca reintentar. Y como
            // todas fallarían igual, parar aquí conserva el orden de captura.
            if (siguiente.nextTryAt && Date.parse(siguiente.nextTryAt) > Date.now()) break;

            try {
              await fireKitchenOrder(siguiente.payload);
              set((s) => ({ comandas: sin(s.comandas) }));
              confirmadas++;
              continue;
            } catch (err) {
              if (isOfflineError(err)) return confirmadas;   // volvió a caerse

              const reintentable = err.response?.data?.retriable === true;
              const vencida = Date.now() - Date.parse(siguiente.since) > MAX_ESPERA_MS;
              const motivo  = err.response?.data?.error ?? err.message;

              // Que el negocio no esté listo —sin caja abierta— no es una
              // comanda mala: es la misma familia que "no hay red". Se espera.
              if (reintentable && !vencida) {
                const intentos = (siguiente.attempts ?? 0) + 1;
                set((s) => ({
                  comandas: s.comandas.map((c) => (c === siguiente
                    ? { ...siguiente, attempts: intentos, lastError: motivo,
                        nextTryAt: new Date(Date.now() + esperaTras(intentos)).toISOString() }
                    : c)),
                }));
                break;   // las de atrás fallarían igual
              }

              // Rechazada de verdad, o esperó tanto que su servicio ya cerró.
              set((s) => ({
                comandas: sin(s.comandas),
                fallidas: [...(s.fallidas ?? []), {
                  ...siguiente,
                  lastError: vencida
                    ? `Esperó más de 4 horas sin poder entrar (${motivo})`
                    : motivo,
                  falloAt: new Date().toISOString(),
                }],
              }));
            }
          }

          const porId = new Map(remotas.map((t) => [t.id, t]));

          for (const id of ids) {
            const entrada = get().pendientes[id];
            if (!entrada) continue;                     // se resolvió mientras tanto

            const remota = porId.get(id);
            if (!remota) {
              // La cuenta ya no está abierta: se cobró o se borró en otro
              // dispositivo. Lo local dejó de tener a dónde ir.
              get().quitar(id);
              continue;
            }

            const items = mergeTabItems(entrada.baseItems ?? [], entrada.items, remota.items);

            try {
              const guardada = await apiUpdateTab(id, {
                items,
                customer:   entrada.customer,
                comensales: entrada.comensales,
                discount:   entrada.discount,
                note:       entrada.note,
                version:  remota.version,
              });
              get().quitar(id);
              alConfirmar?.(guardada);
              confirmadas++;
            } catch (err) {
              get()._marcarFallo(id, err);
              // Sin red no tiene caso seguir con las demás.
              if (isOfflineError(err)) break;
            }
          }
        } finally {
          set({ drenando: false });
          get()._publicarContadores();
          if (confirmadas) useSyncStore.getState().setSyncedAt();
        }

        return confirmadas;
      },

      _marcarFallo: (tabId, err) => {
        const codigo = err.response?.status;
        const entrada = get().pendientes[tabId];
        if (!entrada) return;

        const attempts = entrada.attempts + 1;
        const rendirse = (codigo && DEFINITIVOS.includes(codigo) && codigo !== 409)
          || attempts >= MAX_INTENTOS;

        // El 409 no es rendirse: significa que otra tablet guardó primero, y
        // el siguiente drenado ya funde contra esa versión nueva.
        if (rendirse && codigo !== 409) {
          set((s) => ({
            pendientes: {
              ...s.pendientes,
              [tabId]: { ...entrada, attempts, lastError: err.response?.data?.error ?? err.message, fallida: true },
            },
          }));
          return;
        }

        set((s) => ({
          pendientes: {
            ...s.pendientes,
            [tabId]: { ...entrada, attempts, lastError: err.response?.data?.error ?? err.message },
          },
        }));
      },

      // Descarta una pendiente que ya no se va a poder subir. Es una decisión
      // del usuario, no automática: implica perder esos platillos.
      descartar: (tabId) => get().quitar(tabId),

      // Devuelve una comanda apartada al frente de la cola. El `client_uuid`
      // es el mismo, así que si en realidad sí había llegado, el servidor
      // responde `created:false` y no se duplica el platillo.
      // Reciben la entrada misma y no su posición: la pantalla enseña solo las
      // de este comercio, así que su posición no es la de la lista completa.
      reintentarComanda: (fallida) => {
        if (!fallida || !get().fallidas?.includes(fallida)) return;
        set((s) => ({
          fallidas: s.fallidas.filter((f) => f !== fallida),
          comandas: [{ ...fallida, attempts: 0, nextTryAt: null, lastError: null }, ...s.comandas],
        }));
        get()._publicarContadores();
        get().drenar().catch(() => {});
      },

      descartarComanda: (fallida) => {
        set((s) => ({ fallidas: s.fallidas.filter((f) => f !== fallida) }));
        get()._publicarContadores();
      },

      // Lo que se cuenta y se enseña es solo lo de este comercio: lo de otros
      // no es asunto de quien está adentro.
      _publicarContadores: () => {
        const st = get();
        const todas = Object.values(st.pendientes).filter((e) => esDelComercio(e));
        useSyncStore.getState().setOutbox(
          todas.filter((e) => !e.fallida).length + st.comandas.filter((c) => esDelComercio(c)).length,
          todas.filter((e) => e.fallida).length + (st.fallidas ?? []).filter((c) => esDelComercio(c)).length,
        );
      },

      // Lo que quedó en la cola de una versión anterior, sin comercio. Si el
      // aparato sigue dentro de un comercio, es de ese: no se pudo cambiar de
      // comercio sin pasar por el login, y la versión anterior lo habría
      // mandado ahí. Si no hay nadie dentro, no hay forma de saberlo: las
      // comandas se apartan para que una persona decida y las cuentas se
      // marcan como fallidas, que es como se enseñan para decidir.
      adoptarSinComercio: () => {
        const comercio = comercioActual();
        const st = get();
        const sinMarca = (e) => e && e.tenantId == null;
        if (!Object.values(st.pendientes).some(sinMarca) && !st.comandas.some(sinMarca) && !(st.fallidas ?? []).some(sinMarca)) return;

        if (comercio) {
          const marcar = (e) => (sinMarca(e) ? { ...e, tenantId: comercio } : e);
          set({
            pendientes: Object.fromEntries(Object.entries(st.pendientes).map(([k, e]) => [k, marcar(e)])),
            comandas:   st.comandas.map(marcar),
            fallidas:   (st.fallidas ?? []).map(marcar),
          });
        } else {
          const aviso = 'Quedó de una versión anterior sin saber de qué comercio es. Revisa antes de reintentar.';
          set({
            pendientes: Object.fromEntries(Object.entries(st.pendientes).map(([k, e]) => [k, sinMarca(e) ? { ...e, fallida: true, lastError: aviso } : e])),
            comandas:   st.comandas.filter((c) => !sinMarca(c)),
            fallidas:   [...(st.fallidas ?? []), ...st.comandas.filter(sinMarca).map((c) => ({ ...c, lastError: aviso, falloAt: new Date().toISOString() }))],
          });
        }
        get()._publicarContadores();
      },

      // Cuántos cambios sin confirmar tiene ESTA cuenta. Lo usa el panel del
      // carrito para decir "pendiente" en vez de dar por guardado.
      pendienteDe: (tabId) => get().pendientes[tabId] ?? null,
    }),
    {
      name: 'gc-pos-outbox',
      // `drenando` es de esta ejecución: si la app se cierra a media subida,
      // al abrir hay que poder volver a intentar.
      partialize: (s) => ({ pendientes: s.pendientes, comandas: s.comandas, fallidas: s.fallidas ?? [] }),
    }
  )
);

let intervalo = null;

/**
 * Arranca el drenado. Se llama una vez al iniciar la app.
 *
 * Tres disparadores porque ninguno basta solo: `online` no siempre se dispara
 * (el wifi puede seguir asociado y no haber ruta), el intervalo cubre eso, y
 * el arranque cubre el caso de que el mesero haya cerrado la app con órdenes
 * sin subir — que es justo cuando más importa.
 */
export const iniciarDrenado = ({ cadaMs = 15000 } = {}) => {
  const drenar = () => useOutboxStore.getState().drenar().catch(() => {});

  // Antes del primer envío: lo que venga de una versión sin marca de comercio.
  useOutboxStore.getState().adoptarSinComercio();

  drenar();
  window.addEventListener('online', drenar);
  if (intervalo) clearInterval(intervalo);
  intervalo = setInterval(() => {
    const st = useOutboxStore.getState();
    if (Object.values(st.pendientes).some((e) => esDelComercio(e)) || st.comandas.some((c) => esDelComercio(c))) drenar();
  }, cadaMs);

  useOutboxStore.getState()._publicarContadores();

  return () => {
    window.removeEventListener('online', drenar);
    if (intervalo) clearInterval(intervalo);
    intervalo = null;
  };
};
